/**
 * Hop feedback for screen actions.
 *
 * `data-krs-state` belongs to blockedState (blocked + aria-disabled). This
 * module only writes `data-krs-hop`, so a blocked control keeps its disabled
 * look during a hop, and only re-enables the controls it disabled itself.
 */

const HOP_STATE = "data-krs-hop";
const HOP_DISABLED = "data-krs-hop-disabled";
const HOP_LOADING = "loading";
const HOP_PENDING = "pending";
const ACTION_NEXT = "next";
const ACTION_BACK = "back";
const HOP_STYLE_ID = "krs-hop-busy";
const HOP_STYLE = `
[data-krs-hop="pending"] {
  opacity: .55;
  cursor: default;
  pointer-events: none;
}
[data-krs-hop="loading"] {
  position: relative;
  pointer-events: none;
}
[data-krs-hop="loading"]::after {
  content: "";
  position: absolute;
  top: 50%;
  right: 16px;
  width: 18px;
  height: 18px;
  margin-top: -9px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: krs-spin .7s linear infinite;
}
[data-value][data-krs-hop="loading"]::after {
  left: 16px;
  right: auto;
}
@keyframes krs-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  [data-krs-hop="loading"]::after { animation: none; }
}
`;

const isChoiceCard = (element) =>
  Boolean(element && element.hasAttribute("data-value"));

const isBlocked = (element) =>
  element.getAttribute("data-krs-state") === "blocked" ||
  element.getAttribute("aria-disabled") === "true";

const isPrimaryAction = (element) =>
  element.getAttribute("data-action") === ACTION_NEXT ||
  element.getAttribute("data-kayros-slot") === "action-next";

const ensureHopStyle = (doc) => {
  if (!doc || !doc.getElementById || doc.getElementById(HOP_STYLE_ID)) {
    return;
  }
  const style = doc.createElement("style");
  style.id = HOP_STYLE_ID;
  style.textContent = HOP_STYLE;
  (doc.head || doc.body).appendChild(style);
};

const slotElement = (doc, slot) => {
  if (!slot || slot.type !== "action") return null;
  const selector = slot.selector || `[data-kayros-slot='${slot.id}']`;
  return doc.querySelector(selector);
};

const collectActionElements = (doc, slots) => {
  const elements = new Set();
  for (const slot of slots || []) {
    const element = slotElement(doc, slot);
    if (element) elements.add(element);
  }
  if (doc.querySelectorAll) {
    const extras = doc.querySelectorAll(
      `[data-action='${ACTION_NEXT}'], [data-action='${ACTION_BACK}']`,
    );
    for (const element of extras) elements.add(element);
  }
  return [...elements];
};

const applyHopState = (element, state) => {
  element.setAttribute(HOP_STATE, state);
  if (state === HOP_LOADING) element.setAttribute("aria-busy", "true");
  if (element.disabled) return;
  element.disabled = true;
  element.setAttribute(HOP_DISABLED, "true");
};

const clearHopState = (element) => {
  if (!element.hasAttribute(HOP_STATE)) return;
  element.removeAttribute(HOP_STATE);
  element.removeAttribute("aria-busy");
  if (!element.hasAttribute(HOP_DISABLED)) return;
  element.removeAttribute(HOP_DISABLED);
  element.disabled = false;
};

/**
 * Flag the control the user clicked so the spinner lands on it instead of the
 * screen's primary action. Called before the run hop starts.
 */
export const markHopLoading = (element) => {
  if (!element || element.disabled || isBlocked(element)) return false;
  element.setAttribute(HOP_STATE, HOP_LOADING);
  return true;
};

/**
 * Hop visuals belong to the screen the user clicked. A later hydrate
 * (optimistic next/back) must not inherit them: that is how a spinner
 * appeared on the destination Continuer.
 */
export const resolveHopOrigin = (busy, screenKey, originKey) => {
  if (!busy) return { busy: false, originKey: null };
  const nextOrigin = originKey == null ? screenKey : originKey;
  return { busy: nextOrigin === screenKey, originKey: nextOrigin };
};

export const applyHopBusy = (doc, slots, busy, options = {}) => {
  if (!doc) return;
  ensureHopStyle(doc);
  const elements = collectActionElements(doc, slots);
  if (!busy) {
    elements.forEach(clearHopState);
    return;
  }
  const clicked = elements.find(
    (element) => element.getAttribute(HOP_STATE) === HOP_LOADING,
  );
  const inventLoader = options.inventLoader !== false;
  const loading =
    clicked ||
    (inventLoader
      ? elements.find(
          (element) =>
            isPrimaryAction(element) &&
            !isChoiceCard(element) &&
            !isBlocked(element),
        )
      : null);
  if (!loading && !inventLoader) {
    elements.forEach(clearHopState);
    return;
  }
  for (const element of elements) {
    if (element !== loading && isBlocked(element)) continue;
    applyHopState(element, element === loading ? HOP_LOADING : HOP_PENDING);
  }
};
